import { BreakpointObserver } from '@angular/cdk/layout';
import { inject, Injectable, Injector, TemplateRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { _IdGenerator } from '@angular/cdk/a11y';
import { NttToastContainer, ToastItem } from './toast-container';
import {
  NTT_TOAST_CONFIG,
  NTT_TOAST_DEFAULT_CONFIG,
  NttToastGlobalConfig,
  ResponsiveToastPosition,
  ToastAction,
  ToastPosition,
  ToastType,
} from './toast.config';

export interface ToastConfig {
  type?: ToastType;
  message: string;
  duration?: number;
  position?: ToastPosition | ResponsiveToastPosition;
  dismissible?: boolean;
  pauseOnHover?: boolean;
  actionsTemplate?: TemplateRef<unknown>;
  action?: ToastAction;
}

interface PositionContainer {
  overlayRef: OverlayRef;
  containerInstance: NttToastContainer;
}

@Injectable({
  providedIn: 'root',
})
export class NttToastService {
  private readonly _globalConfig = inject(NTT_TOAST_CONFIG);
  private readonly _idGenerator = inject(_IdGenerator);
  private readonly _overlay = inject(Overlay);
  private readonly _injector = inject(Injector);
  private readonly _breakpointObserver = inject(BreakpointObserver);

  private readonly _containers = new Map<ToastPosition, PositionContainer>();

  private get _mergedGlobalConfig(): Required<NttToastGlobalConfig> {
    return {
      ...NTT_TOAST_DEFAULT_CONFIG,
      ...this._globalConfig,
    };
  }

  show(config: ToastConfig): string {
    const id = this._generateId();
    const globalConfig = this._mergedGlobalConfig;

    const resolvedPosition = this._resolvePosition(
      config.position ?? globalConfig.position,
    );

    const container = this._getOrCreateContainer(resolvedPosition);

    const toast: ToastItem = {
      id,
      type: config.type ?? globalConfig.type,
      message: config.message,
      duration: config.duration ?? globalConfig.duration,
      dismissible: config.dismissible ?? globalConfig.dismissible,
      pauseOnHover: config.pauseOnHover ?? globalConfig.pauseOnHover,
      timer: null,
      paused: false,
      startTime: Date.now(),
      remaining: config.duration ?? globalConfig.duration,
      actionsTemplate: config.actionsTemplate,
      action: config.action,
    };

    container.containerInstance.add(toast);

    return id;
  }

  dismiss(id: string): void {
    this._containers.forEach((container) => {
      container.containerInstance.remove(id);
    });
  }

  showSuccess(
    message: string,
    config?: Partial<Pick<ToastConfig, 'duration' | 'position'>>,
  ): string {
    return this.show({ type: 'success', message, ...config });
  }

  showInfo(
    message: string,
    config?: Partial<Pick<ToastConfig, 'duration' | 'position'>>,
  ): string {
    return this.show({ type: 'info', message, ...config });
  }

  showWarning(
    message: string,
    config?: Partial<Pick<ToastConfig, 'duration' | 'position'>>,
  ): string {
    return this.show({ type: 'warning', message, ...config });
  }

  showError(
    message: string,
    config?: Partial<Pick<ToastConfig, 'duration' | 'position'>>,
  ): string {
    return this.show({ type: 'error', message, ...config });
  }

  showDefault(
    message: string,
    config?: Partial<Pick<ToastConfig, 'duration' | 'position'>>,
  ): string {
    return this.show({ type: 'default', message, ...config });
  }

  private _resolvePosition(
    position: ToastPosition | ResponsiveToastPosition,
  ): ToastPosition {
    if (typeof position === 'string') {
      return position;
    }
    const isMobile = this._breakpointObserver.isMatched('(max-width: 640px)');
    return isMobile ? position.mobile : position.desktop;
  }

  private _generateId(): string {
    return this._idGenerator.getId('toast-');
  }

  private _getOrCreateContainer(position: ToastPosition): PositionContainer {
    if (this._containers.has(position)) {
      return this._containers.get(position)!;
    }

    const overlayRef = this._overlay.create({
      positionStrategy: this._getPositionStrategy(position),
      panelClass: [`ntt-toast-container--${position}`],
      hasBackdrop: false,
    });

    const portal = new ComponentPortal(NttToastContainer, null, this._injector);
    const componentRef = overlayRef.attach(portal);
    componentRef.setInput('position', position);

    const container: PositionContainer = {
      overlayRef,
      containerInstance: componentRef.instance,
    };

    this._containers.set(position, container);
    return container;
  }

  private _getPositionStrategy(position: ToastPosition) {
    const globalConfig = this._mergedGlobalConfig;
    const strategy = this._overlay.position().global();
    const verticalOffset = `${globalConfig.verticalOffset}px`;
    const horizontalOffset = `${globalConfig.horizontalOffset}px`;

    const isTop = position.startsWith('top');

    if (isTop) {
      switch (position) {
        case 'top-left':
          return strategy.top(verticalOffset).left(horizontalOffset);
        case 'top-center':
          return strategy.top(verticalOffset).centerHorizontally();
        case 'top-right':
          return strategy.top(verticalOffset).right(horizontalOffset);
      }
    } else {
      switch (position) {
        case 'bottom-left':
          return strategy.bottom(verticalOffset).left(horizontalOffset);
        case 'bottom-center':
          return strategy.bottom(verticalOffset).centerHorizontally();
        case 'bottom-right':
          return strategy.bottom(verticalOffset).right(horizontalOffset);
      }
    }

    return strategy.top(verticalOffset).right(horizontalOffset);
  }
}
